import { Injectable, OnDestroy } from '@angular/core';
import { AngularFireDatabase } from '@newgenus/fire-compat/database';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import firebase from 'firebase/compat/app';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

/**
 * Presence data stored in Firebase Realtime Database.
 */
export interface PresenceRecord {
    online: boolean;
    lastSeen: number; // Server timestamp (ms)
    displayName?: string;
}

/**
 * PresenceService — tracks which users are online using Firebase Realtime Database.
 *
 * HOW IT WORKS:
 * 1. On auth, writes `{ online: true, lastSeen: serverTimestamp }` to `presence/{orgKey}/{userId}`.
 * 2. Registers an `onDisconnect()` handler so RTDB auto-sets `online: false` when the
 *    client disconnects (tab close, network loss, etc.).
 * 3. Listens to `.info/connected` to detect reconnections after network loss or long idle
 *    and re-writes `online: true` each time.
 * 4. Exposes an observable of online user IDs for a given org so widgets can show indicators.
 */
@Injectable({ providedIn: 'root' })
export class PresenceService implements OnDestroy {
    /** Set of currently online user IDs for the active org */
    private onlineUsersSubject = new BehaviorSubject<Set<string>>(new Set());
    public onlineUsers$: Observable<Set<string>> = this.onlineUsersSubject.asObservable();

    private presenceRef: firebase.database.Reference | null = null;
    private orgPresenceRef: firebase.database.Reference | null = null;
    private connectedSub: Subscription | null = null;
    private currentOrgKey: string | null = null;
    private currentUserId: string | null = null;
    private currentDisplayName: string | undefined;

    /** Interval handle for periodic heartbeat writes */
    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    /** Heartbeat interval — re-write online status every 2 minutes to survive idle/sleep */
    private static readonly HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;

    constructor(
        private rtdb: AngularFireDatabase,
        private afAuth: AngularFireAuth
    ) {}

    /**
     * Start tracking presence for a user within an organization.
     * Call this once the user and org are known (e.g., on dashboard load).
     *
     * Safe to call repeatedly with the same arguments — it will re-assert the
     * online status without tearing down and recreating listeners.
     */
    public connect(orgKey: string, userId: string, displayName?: string): void {
        // Same user+org: just re-assert online status (handles page refresh / wake from sleep)
        if (this.currentOrgKey === orgKey && this.currentUserId === userId) {
            this.assertOnline(displayName);
            return;
        }

        // Different user or org — full teardown and reconnect
        this.disconnect();

        this.currentOrgKey = orgKey;
        this.currentUserId = userId;
        this.currentDisplayName = displayName;

        // Reference for THIS user's presence node
        this.presenceRef = this.rtdb.database.ref(`presence/${orgKey}/${userId}`);

        // Listen to the special .info/connected path — true when connected to RTDB.
        // This fires on initial connect AND every reconnect after network loss / sleep.
        this.connectedSub = this.rtdb
            .object<boolean>('.info/connected')
            .valueChanges()
            .subscribe((connected) => {
                if (connected && this.presenceRef) {
                    // When we disconnect, mark offline
                    this.presenceRef.onDisconnect().set({
                        online: false,
                        lastSeen: firebase.database.ServerValue.TIMESTAMP,
                    } as PresenceRecord);

                    // Set online now
                    this.writeOnline(displayName);
                }
            });

        // Start a periodic heartbeat so long-idle tabs that never fully disconnect
        // (e.g., laptop sleep where onDisconnect fires but .info/connected doesn't
        // re-emit on wake) still re-assert their online status.
        this.startHeartbeat();

        // Listen to ALL users' presence in this org to build the online set
        this.orgPresenceRef = this.rtdb.database.ref(`presence/${orgKey}`);
        this.orgPresenceRef.on('value', (snapshot) => {
            const onlineSet = new Set<string>();
            if (snapshot.exists()) {
                snapshot.forEach((child) => {
                    const val = child.val() as PresenceRecord;
                    const childKey = child.key;
                    if (val?.online === true && childKey) {
                        onlineSet.add(childKey);
                    }
                });
            }
            this.onlineUsersSubject.next(onlineSet);
        });
    }

    /**
     * Re-assert online status without tearing down listeners.
     * Used when connect() is called again with the same user+org
     * (e.g. after page refresh, browser wake from sleep, or org store re-emission).
     */
    private assertOnline(displayName?: string): void {
        if (this.presenceRef) {
            // Re-register onDisconnect in case the previous one already fired
            this.presenceRef.onDisconnect().set({
                online: false,
                lastSeen: firebase.database.ServerValue.TIMESTAMP,
            } as PresenceRecord);

            this.writeOnline(displayName);
        }
    }

    /** Write the online record to RTDB */
    private writeOnline(displayName?: string): void {
        if (!this.presenceRef) return;
        this.presenceRef.set({
            online: true,
            lastSeen: firebase.database.ServerValue.TIMESTAMP,
            ...(displayName ? { displayName } : {}),
        } as PresenceRecord);
    }

    /** Start periodic heartbeat to re-assert online status */
    private startHeartbeat(): void {
        this.stopHeartbeat();
        this.heartbeatTimer = setInterval(() => {
            this.assertOnline(this.currentDisplayName);
        }, PresenceService.HEARTBEAT_INTERVAL_MS);
    }

    /** Stop the heartbeat timer */
    private stopHeartbeat(): void {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    /**
     * Stop tracking presence and mark user offline.
     */
    public disconnect(): void {
        this.stopHeartbeat();

        if (this.connectedSub) {
            this.connectedSub.unsubscribe();
            this.connectedSub = null;
        }

        // Remove the value listener from the org presence path
        if (this.orgPresenceRef) {
            this.orgPresenceRef.off('value');
            this.orgPresenceRef = null;
        }

        // Mark the user offline explicitly
        if (this.presenceRef) {
            this.presenceRef.set({
                online: false,
                lastSeen: firebase.database.ServerValue.TIMESTAMP,
            } as PresenceRecord);
            this.presenceRef.onDisconnect().cancel();
            this.presenceRef = null;
        }

        this.currentOrgKey = null;
        this.currentUserId = null;
        this.currentDisplayName = undefined;
        this.onlineUsersSubject.next(new Set());
    }

    /**
     * Check if a specific user is online.
     */
    public isOnline(userId: string): boolean {
        return this.onlineUsersSubject.value.has(userId);
    }

    /**
     * Get the count of online users.
     */
    public get onlineCount(): number {
        return this.onlineUsersSubject.value.size;
    }

    ngOnDestroy(): void {
        this.disconnect();
    }
}
